import { useRef, useMemo } from "react";
import { useFrame } from "@react-three/fiber";
import { Html } from "@react-three/drei";
import * as THREE from "three";

interface ParticleSphereProps {
  rotationDirection: number;
  isPaused: boolean;
  particleCount: number;
  rotationSpeed: number;
  waveAmplitude: number;
  particleSize: number;
  clustering: number;
  particleColor: string;
}

const ParticleSphere: React.FC<ParticleSphereProps> = ({
  rotationDirection,
  isPaused,
  particleCount,
  rotationSpeed,
  waveAmplitude,
  particleSize,
  clustering,
  particleColor,
}) => {
  const meshRef = useRef<THREE.InstancedMesh>(null);

  // Create particle positions with clustering towards poles (top and bottom)
  const { particlesPosition, originalPositions } = useMemo(() => {
    const positions = new Float32Array(particleCount * 3);

    for (let i = 0; i < particleCount; i++) {
      // Generate spherical coordinates
      const theta = Math.random() * Math.PI * 2;

      // Cluster particles towards poles by biasing phi towards 0 and PI
      // Using a power function to push values away from the middle (PI/2)
      const rand = Math.random();
      const clusteredPhi =
        rand < 0.5
          ? (Math.pow(rand * 2, clustering) * Math.PI) / 2 // Cluster towards top pole (phi = 0)
          : Math.PI - (Math.pow((1 - rand) * 2, clustering) * Math.PI) / 2; // Cluster towards bottom pole (phi = PI)

      const radius = 2;

      // Convert spherical to cartesian coordinates
      // Y-axis is vertical (poles at top/bottom)
      const x = radius * Math.sin(clusteredPhi) * Math.cos(theta);
      const y = radius * Math.cos(clusteredPhi);
      const z = radius * Math.sin(clusteredPhi) * Math.sin(theta);

      positions[i * 3] = x;
      positions[i * 3 + 1] = y;
      positions[i * 3 + 2] = z;
    }

    return {
      particlesPosition: positions,
      originalPositions: new Float32Array(positions),
    };
  }, [particleCount, clustering]);

  const offset: number[] = useMemo(
    () =>
      Array.from({ length: particleCount }).map(
        () => Math.random() * Math.PI * 2,
      ),
    [particleCount],
  );

  // Rotate the sphere and animate wave motion
  useFrame((_state, delta) => {
    if (meshRef.current && !isPaused) {
      meshRef.current.rotation.y += delta * rotationSpeed * rotationDirection;

      const matrix = new THREE.Matrix4();
      const rotationY = meshRef.current.rotation.y;

      const rotationDegrees =
        ((((rotationY * 180) / Math.PI) % 360) + 360) % 360;

      for (let i = 0; i < particleCount; i++) {
        const i3 = i * 3;
        // Use original positions from particlesPosition
        const x = particlesPosition[i3];
        const baseY = originalPositions[i3 + 1];
        const z = particlesPosition[i3 + 2];

        // Calculate distance from poles (0 at poles, 1 at equator)
        const distanceFromPole = Math.sqrt(x * x + z * z) / 2; // radius is 2

        // Create wave that travels around the sphere as it rotates
        const wave =
          Math.sin((rotationDegrees * 10 * Math.PI) / 360 + offset[i]) *
          distanceFromPole *
          waveAmplitude;

        // Apply wave to Y position
        const finalY = baseY + wave;

        // Set instance matrix
        matrix.setPosition(x, finalY, z);
        meshRef.current.setMatrixAt(i, matrix);
      }

      meshRef.current.instanceMatrix.needsUpdate = true;
    }
  });

  return (
    <>
      <instancedMesh ref={meshRef} args={[undefined, undefined, particleCount]}>
        <sphereGeometry args={[particleSize, 8, 8]} />
        <meshBasicMaterial color={particleColor} transparent={true} opacity={0.8} />
      </instancedMesh>
      <Html style={{ position: "absolute", bottom: "0", right: "20px" }}></Html>
    </>
  );
};

export default ParticleSphere;
